<script>
    import { _, number } from 'svelte-i18n';
    import { onMount, getContext } from 'svelte';
    import { goTo } from '../../utils.js';
    import { resourceTroveBonuses, castleLevel, storePacks, gameConfig } from '../../stores';
    import Fa from 'svelte-fa';
    import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
    import Starburst from '../Starburst.svelte';

    const { buyPack: buyPackFromContext } = getContext('buyPack');

    // --- Timer ---
    let timeLeft = $state('');
    let timerInterval = $state(null);

    function getNextMidnight() {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        return midnight.getTime();
    }

    function updateTimer() {
        const remaining = getNextMidnight() - Date.now();
        if (remaining <= 0) { timeLeft = '00:00:00'; return; }
        const h = Math.floor(remaining / 3600000);
        const m = Math.floor((remaining % 3600000) / 60000);
        const s = Math.floor((remaining % 60000) / 1000);
        timeLeft = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    }

    // --- Bonus ---
    let currentBonus = $derived.by(() => {
        const bonuses = $resourceTroveBonuses;
        const level = $castleLevel;
        if (!bonuses || !level) return 0;
        let bonus = 0;
        for (const [lvl, pct] of Object.entries(bonuses)) {
            if (level >= parseInt(lvl)) bonus = pct;
        }
        return bonus;
    });

    // --- Packs ---
    let resourcePacks = $derived(
        $storePacks
            .filter(p => p.storeSection === 'resourceTrove')
            .filter(p => !p.endAt || new Date(p.endAt).getTime() > Date.now())
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    );

    let buyAllPack = $derived.by(() => {
        if (resourcePacks.length === 0) return null;
        let mostExpensive = null;
        let highestPrice = -1;
        for (const pack of resourcePacks) {
            const amount = pack.paymentHub?.price?.amount ?? 0;
            if (amount > highestPrice) {
                highestPrice = amount;
                mostExpensive = pack;
            }
        }
        return mostExpensive;
    });

    let individualPacks = $derived(
        resourcePacks.filter(p => buyAllPack && p !== buyAllPack)
    );

    function getResourceInfo(pack) {
        const bn = (pack.bundleName || '').toLowerCase();
        if (bn.includes('food'))                                                        return { name: 'Food',       img: '/images/advancement-rewards/resource_food.png' };
        if (bn.includes('wood'))                                                        return { name: 'Wood',       img: '/images/advancement-rewards/resource_wood.png' };
        if (bn.includes('iron'))                                                        return { name: 'Iron',       img: '/images/advancement-rewards/resource_iron.png' };
        if (bn.includes('cotton'))                                                      return { name: 'Cotton',     img: '/images/advancement-rewards/resource_stone.png' };
        if (bn.includes('stone'))                                                       return { name: 'Cotton',      img: '/images/advancement-rewards/resource_stone.png' };
        if (bn.includes('silver') || bn.includes('green') || bn.includes('greenback')) return { name: 'Greenbacks', img: '/images/advancement-rewards/resource_silver.png' };

        // Fallback via gameConfig resourceType
        const item = pack.items?.[0];
        const gameItem = $gameConfig?.gameItems?.[item?.id];
        const map = {
            1: { name: 'Cotton',      img: '/images/advancement-rewards/resource_stone.png' },
            2: { name: 'Wood',       img: '/images/advancement-rewards/resource_wood.png' },
            3: { name: 'Iron',       img: '/images/advancement-rewards/resource_iron.png' },
            4: { name: 'Food',       img: '/images/advancement-rewards/resource_food.png' },
            5: { name: 'Greenbacks', img: '/images/advancement-rewards/resource_silver.png' },
        };
        return map[gameItem?.resourceType] ?? { name: gameItem?.name || 'Resources', img: '/images/advancement-rewards/resource_food.png' };
    }

    function getPriceString(pack) {
        if (!pack?.paymentHub?.price || pack.paymentHub.price.amount === 'missing') return '—';
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: pack.paymentHub.price.currency,
        }).format(pack.paymentHub.price.amount / 100);
    }

    function getQty(pack) {
        const item = pack.items?.[0];
        if (!item) return null;
        const gameItem = $gameConfig?.gameItems?.[item.id];
        const qty = gameItem?.quantity ?? null;
        if (!qty) return null;
        return qty.toLocaleString();
    }

    function buyPack(pack) {
        buyPackFromContext({
            detail: {
                storePack: pack,
                packDetails: { title: getResourceInfo(pack).name, description: '', subTitle: '' },
            }
        });
    }

    onMount(() => {
        window.scrollTo(0, 0);
        goTo('/supplies/advancementRewards?tab=resourceTrove', true);
        updateTimer();
        timerInterval = setInterval(updateTimer, 1000);
        return () => clearInterval(timerInterval);
    });
</script>

<div class="rt-container">
    <!-- Hero -->
    <div class="hero">
        <div class="hero-content">
            <div class="hero-left">
                {#if timeLeft && individualPacks.length > 0}
                    <div class="timer">
                        <span class="timer-label">{$_('TimeLeft')}:</span>
                        <span class="timer-value">{timeLeft}</span>
                    </div>
                {/if}
                <h1>{$_('resourceTroveTitle')}</h1>
                <p>{$_('resourceTroveDescription')}</p>
            </div>
            {#if individualPacks.length > 0}
                <div class="hero-right">
                    <div class="buy-all-wrap">
                        {#if buyAllPack?.discount > 0}
                            <div class="starburst-pos">
                                <Starburst label="Discount">
                                    <div>-{buyAllPack.discount}%</div>
                                </Starburst>
                            </div>
                        {/if}
                        <button
                            class="button primary orange buy-all-btn"
                            onclick={() => buyPack(buyAllPack)}
                            disabled={!buyAllPack}
                        >
                            <span class="buy-all-label">{$_('resourceTroveBuyAllButton')}</span>
                            <span class="buy-all-price">{buyAllPack ? getPriceString(buyAllPack) : '—'}</span>
                        </button>
                    </div>
                </div>
            {/if}
        </div>
    </div>

    <!-- Bonus banner -->
    {#if currentBonus > 0 && individualPacks.length > 0}
        <div class="bonus-banner">
            <span>{$_('resourceTroveBuyAllDescription', { values: { level: $castleLevel, bonus: currentBonus } })}</span>
        </div>
    {/if}

    <!-- Resource rows -->
    {#if $storePacks.length === 0}
        <div class="loading-state">
            <div class="spinner"></div>
        </div>
    {:else if individualPacks.length === 0}
        <div class="purchased-state">
            <Fa icon={faCircleCheck} size="3x" primaryColor="#4a9e4a" />
            <h3>{$_('AllPacksPurchased')}</h3>
            <p>{$_('AllPacksPurchasedDescription')}</p>
        </div>
    {:else}
        <div class="rows-list">
            {#each individualPacks as pack, i (pack.bundleName + '-' + i)}
                {@const info = getResourceInfo(pack)}
                {@const qty = getQty(pack)}
                <div class="resource-row" style="animation-delay:{i*40}ms">
                    <div class="res-icon-wrap">
                        <img src={info.img} alt={info.name} class="res-img" />
                    </div>
                    <div class="res-text">
                        <span class="res-qty">{#if qty}{qty}{/if} {info.name}</span>
                    </div>
                    <div class="res-action">
                        {#if pack.paymentHub?.price}
                            <button class="button primary orange res-buy-btn" onclick={() => buyPack(pack)}>
                                {getPriceString(pack)}
                            </button>
                        {:else}
                            <span class="no-price">—</span>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .rt-container {
        max-width: 680px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
    }

    /* ── Hero ── */
    .hero {
        position: relative;
        width: 100%;
        height: 200px;
        background-image: url('/images/permanent_resource_trove_bg.png');
        background-size: cover;
        background-position: center center;
        overflow: hidden;
    }

    .hero::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, rgba(0,5,20,0.15) 0%, rgba(0,5,20,0.52) 50%, rgba(0,5,10,0.9) 100%);
    }

    .hero-content {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 14px 18px;
        gap: 12px;
    }

    .hero-left { display: flex; flex-direction: column; gap: 2px; }

    .timer {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 4px;
    }

    .timer-label {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.2rem;
        color: rgba(255,255,255,0.82);
        text-shadow: 0 1px 2px rgba(0,0,0,0.6);
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .timer-value {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.2rem;
        font-weight: 700;
        color: #f7d25f;
        text-shadow: 0 1px 4px rgba(0,0,0,0.8);
        letter-spacing: 0.08em;
    }

    .hero-left h1 {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 2.4rem;
        font-weight: 900;
        color: #fff;
        text-transform: uppercase;
        text-shadow: 0 2px 8px rgba(0,0,0,0.9);
        margin: 0 0 4px;
        line-height: 1;
        letter-spacing: 0.02em;
    }

    .hero-left p {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.2rem;
        color: rgba(255,255,255,0.88);
        margin: 0;
        text-shadow: 0 1px 3px rgba(0,0,0,0.8);
        max-width: 210px;
        line-height: 1.3;
    }

    .hero-right { flex-shrink: 0; }

    .buy-all-wrap { position: relative; }

    .starburst-pos {
        position: absolute;
        top: -12px;
        right: -5px;
        z-index: 2;
        transform: scale(0.72);
        transform-origin: top right;
    }

    .buy-all-btn {
        display: flex !important;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 10px 18px !important;
        min-width: 110px;
        gap: 3px;
        height: auto !important;
    }

    .buy-all-label {
        font-size: 1.2rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
    }

    .buy-all-price {
        font-size: 1.2rem;
        font-weight: 900;
    }

    /* ── Bonus banner ── */
    .bonus-banner {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 11px 16px;
        background: linear-gradient(90deg, #2a7232, #42b04f, #2a7232);
        color: #fff;
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-weight: 700;
        font-size: 1.2rem;
        text-shadow: 0 1px 2px rgba(0,0,0,0.4);
        letter-spacing: 0.01em;
    }

    /* ── Loading ── */
    .loading-state {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: #f5ede0;
    }

    .spinner {
        width: 36px;
        height: 36px;
        border: 4px solid rgba(106, 74, 26, 0.2);
        border-top-color: #8b5e00;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* ── All purchased ── */
    .purchased-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 48px 24px;
        background: #f5ede0;
        text-align: center;
        animation: fadeIn 0.4s ease-out;
    }

    .purchased-state h3 {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.3rem;
        font-weight: 800;
        color: #2a5a2a;
        margin: 0;
        text-shadow: 0 1px 0 rgba(255,255,255,0.5);
    }

    .purchased-state p {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1rem;
        color: #6b5a3a;
        margin: 0;
        max-width: 280px;
        line-height: 1.4;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }

    /* ── Resource rows ── */
    .rows-list {
        display: flex;
        flex-direction: column;
        background: #f5ede0;
    }

    .resource-row {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        border-bottom: 1px solid rgba(0,0,0,0.09);
        animation: rowIn 0.35s ease-out both;
        background: #f7f0e2;
    }

    .resource-row:nth-child(even) { background: #f0e8d6; }
    .resource-row:last-child { border-bottom: none; }

    .res-icon-wrap {
        flex-shrink: 0;
        width: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .res-img {
        width: 68px;
        height: 68px;
        object-fit: contain;
        border-radius: 8px;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    }

    .res-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .res-qty {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-weight: 700;
        font-size: 1.2rem;
        color: #2a1800;
    }

    .res-action { flex-shrink: 0; }

    .res-buy-btn {
        font-size: 1.1rem !important;
        font-weight: 900 !important;
        padding: 0.7rem 1.4rem !important;
        min-width: 90px;
        height: auto !important;
        border-radius: 6px !important;
        line-height: 1.3 !important;
    }

    .no-price {
        color: #7a5a2a;
        opacity: 0.5;
        font-size: 1rem;
    }

    @keyframes rowIn {
        from { opacity: 0; transform: translateX(-10px); }
        to   { opacity: 1; transform: translateX(0); }
    }
</style>
